import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import http from "../../../commonFunctions/httpClient";
import WSButton from "../../../Components/Button/WSButton";
import logo from "../../../images/MELP Logo.svg"

interface PrescriptionItem {
  id: string;
  prescriptionOrderInvoiceId: string;
  ph_ProductId: string;
  ph_Product: {
    id: string;
    productName: string;
    brandName: string;
    batchNumber: string;
    expiryDate: string;
    packSize?: string;
  };
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface PrescriptionOrder {
  id: string;
  prescriptionOrderDate: string;
  status: string;
  notes?: string;
}

interface PrescriptionOrderInvoiceResponse {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  customerName: string;
  address: string;
  email: string | null;
  phoneNumber: string | null;
  subtotal: number;
  tax: number;
  discountAmount: number;
  grandTotal: number;
  totalAmountReceived: number;
  status: string;
  items: PrescriptionItem[];
  prescriptionOrder: PrescriptionOrder;
  prescriptionOrderId: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PrescriptionOrderInvoiceReportProps {
  bookingId: string;
}

export const PrescriptionOrderInvoiceReport: React.FC<PrescriptionOrderInvoiceReportProps> = ({ bookingId }) => {
  const [invoiceData, setInvoiceData] = useState<PrescriptionOrderInvoiceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);

  const fetchInvoiceData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      console.log("🔄 Fetching prescription invoice for BookingId:", bookingId);
      
      const response: any = await http.get(
        `pharmacy/PrescriptionOrderInvoice/Booking/${bookingId}`
      );
      
      console.log("📊 FULL PRESCRIPTION INVOICE API RESPONSE:", response);
      
      const apiDataArray = (response as any)?.data || response || [];
      const firstInvoice = Array.isArray(apiDataArray) ? apiDataArray[0] : apiDataArray;
      
      console.log("🎯 SELECTED FIRST INVOICE:", firstInvoice);
      
      if (firstInvoice && firstInvoice.invoiceNumber) {
        setInvoiceData(firstInvoice);
      } else {
        setError("No valid prescription invoice found for this booking");
        setInvoiceData(null);
      }
      
    } catch (error: any) {
      console.error("❌ PRESCRIPTION INVOICE API ERROR:", error);
      setError("Failed to fetch prescription invoice data");
      setInvoiceData(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) {
      fetchInvoiceData();
    }
  }, [fetchInvoiceData]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const getExpiryStatus = (expiryDate: string) => {
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffTime = expiry.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return "Expired";
      if (diffDays <= 30) return "Expiring Soon";
      return "Valid";
    } catch {
      return "Unknown";
    }
  };

  const handlePrint = useReactToPrint({
    contentRef
  });

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading prescription invoice...</Typography>
      </Box>
    );
  }

  if (error || !invoiceData) {
    return (
      <Box p={4}>
        <Alert severity="error">
          {error || "No prescription invoice data found for this booking"}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Booking ID: {bookingId}
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  const safeItems = invoiceData.items || [];

  return (
    <Box p={4} sx={{ backgroundColor: "#fff", maxWidth: "100%" }}>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <WSButton
          id="printPrescriptionInvoiceBtn"
          size="small"
          label={`Print Invoice #${invoiceData.invoiceNumber}`}
          onClick={handlePrint}
        />
      </Box>

      <Box
        ref={contentRef}
        sx={{ 
          p: 4, 
          width: "100%", 
          maxWidth: "800px", 
          margin: "0 auto",
          bgcolor: "white",
          '@media print': {
            maxWidth: "none",
            width: "100%",
            p: 2,
            margin: 0
          }
        }}
      >
        {/* HEADER */}
        <Grid container sx={{ mb: 3 }}>
  {/* LOGO - TOP CENTER */}
  <Grid item xs={12} textAlign="left" sx={{ mb: -4 }}>
    <Box
      component="img"
      src={logo}
      alt="MELP Pharmacy Logo"
      sx={{
        width: 100,
        height: 100,
        borderRadius: "12px",
        objectFit: "contain",
        
        '@media print': {
          filter: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
        }
      }}
      onError={(e: any) => {
        e.currentTarget.style.display = "none";
      }}
    />
  </Grid>
  
  {/* BOTTOM ROW - MELP Pharmacy LEFT | EMPTY RIGHT */}
  <Grid item xs={12}>
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={6}>
        <Typography fontSize={20} fontWeight="bold" sx={{ color: "#098963", mb: 1 }}>
          MELP Pharmacy
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
          Medical Excellence Local Pharmacy
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          123 Health Street, Kerala - 670001
        </Typography>
        <Typography variant="body2">
          Phone: +91 98765 43210
        </Typography>
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 80 }} />
    </Grid>
  </Grid>
</Grid>

        <Divider sx={{ my: 3 }} />

        {/* INVOICE DETAILS */}
        <Grid container justifyContent="space-between" mb={3}>
          <Grid item xs={6}>
            <Typography fontSize={18} gutterBottom sx={{ fontWeight: 700 }}>
              Invoice #{invoiceData.invoiceNumber}
            </Typography>
            <Typography sx={{ fontWeight: 500, mb: 1 }}>
              <strong>Customer:</strong> {invoiceData.customerName}
            </Typography>
            <Typography sx={{ mb: 0.5 }}>
              <strong>Address:</strong> {invoiceData.address}
            </Typography>
            {invoiceData.phoneNumber && (
              <Typography>
                <strong>Phone:</strong> {invoiceData.phoneNumber}
              </Typography>
            )}
            {invoiceData.email && (
              <Typography>
                <strong>Email:</strong> {invoiceData.email}
              </Typography>
            )}
            <Typography>
              <strong>Prescription Order #:</strong> {invoiceData.prescriptionOrderId.slice(-6).toUpperCase()}
            </Typography>
            {/* {invoiceData.prescriptionOrder?.notes && (
              <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                <strong>Notes:</strong> {invoiceData.prescriptionOrder.notes}
              </Typography>
            )} */}
          </Grid>
          <Grid item xs={6} textAlign="right">
            <Typography fontSize={18} sx={{ fontWeight: 700, mb: 2 }}>
              <strong>Date:</strong> {formatDate(invoiceData.invoiceDate)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Status:</strong> {invoiceData.prescriptionOrder?.status || invoiceData.status}
            </Typography>
            <Chip
              label={invoiceData.prescriptionOrder?.status || invoiceData.status}
              color="success"
              sx={{ fontWeight: 600 }}
            />
          </Grid>
        </Grid>

        {/* ITEMS TABLE */}
        <TableContainer sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#098963" }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="center">Batch</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="center">Expiry</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="center">Qty</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="right">Unit Price</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {safeItems.length > 0 ? (
                safeItems.map((item: PrescriptionItem) => {
                  const expiryStatus = getExpiryStatus(item.ph_Product.expiryDate);
                  return (
                    <TableRow key={item.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Typography variant="body2">{item.ph_Product?.productName || 'N/A'}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {item.ph_Product?.brandName || 'N/A'}
                          {item.ph_Product?.packSize && ` | ${item.ph_Product.packSize}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{item.ph_Product?.batchNumber || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {formatDate(item.ph_Product?.expiryDate || '')}
                        </Typography>
                        {/* <Chip
                          label={expiryStatus}
                          size="small"
                          color={
                            expiryStatus === "Expired"
                              ? "error"
                              : expiryStatus === "Expiring Soon"
                              ? "warning"
                              : "success"
                          }
                          sx={{ mt: 0.5 }}
                        /> */}
                      </TableCell>
                      <TableCell align="center">{item.quantity || 0}</TableCell>
                      <TableCell align="right">
                        ₹{(item.unitPrice || 0).toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                        ₹{(item.totalPrice || 0).toLocaleString("en-IN")}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No items found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* BILL SUMMARY */}
        <Box sx={{ borderTop: "3px solid #098963", pt: 3, mb: 4 }}>
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="right">
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  <strong>Subtotal:</strong> ₹{(invoiceData.subtotal || 0).toLocaleString("en-IN")}
                </Typography>
                <Typography variant="h6" color="error" sx={{ fontWeight: 700, mb: 1 }}>
                  <strong>Discount:</strong> ₹{(invoiceData.discountAmount || 0).toLocaleString("en-IN")}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  <strong>Tax ({invoiceData.tax || 0}%):</strong> ₹{(
                    (invoiceData.grandTotal || 0) - (invoiceData.subtotal || 0) + (invoiceData.discountAmount || 0)
                  ).toLocaleString("en-IN")}
                </Typography>
                <Divider sx={{ my: 1, borderWidth: 2 }} />
                <Typography fontSize={20} color="#098963" sx={{ fontWeight: 900, mb: 1 }}>
                  <strong>Grand Total:</strong> ₹{(invoiceData.grandTotal || 0).toLocaleString("en-IN")}
                </Typography>
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                  <strong>Paid:</strong> ₹{(invoiceData.totalAmountReceived || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* FOOTER */}
        <Divider sx={{ my: 4 }} />
        <Box textAlign="center">
          <Typography variant="h5" color="#098963" sx={{ fontWeight: 700, mb: 2 }}>
            💊 Get well soon 💙
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Thank you for choosing MELP Pharmacy | Kerala
          </Typography>
          <Typography sx={{ 
            fontSize: '0.8rem', 
            color: 'text.secondary', 
            mt: 2,
            '@media print': { fontSize: '0.9rem' }
          }}>
            * This is a computer-generated invoice
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
